import { useId, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { IconCheck, IconChecklist, IconX } from '@tabler/icons-react';
import { PageHeader, useLayout } from '@hana/shell';
import { Button, Modal, Skeleton, StateView, Table, useToast, type Column } from '@hana/ui';
import { useT } from '@hana/i18n';
import './approve.i18n';
import { StateCard, meeStateProps } from '../meeState';
import type { MeeRequest } from '../data';
import { BOARD_TODAY, otHours } from '../data';
import { monthAbbr, num } from '../home/view';
import { StatusChip, TypeIcon } from '../Parts';
import { useMee } from '../useMee';
import s from '../mee.module.css';
import { absenceGrid, dayHead, leftLabel, pendingOfTeam, type AbsenceKind } from './team';

const KIND: Record<Exclude<AbsenceKind, ''>, { k: string; color: string }> = {
  leave: { k: 'Nghỉ phép', color: 'var(--d-blue)' },
  pending: { k: 'Chờ duyệt', color: 'var(--d-amber)' },
  trip: { k: 'Công tác', color: 'var(--d-violet)' },
  off: { k: 'Ngày nghỉ', color: 'var(--d-gray)' },
};
const SPAN = 14;
const fd = (day: string): string => `${day.slice(8, 10)}/${monthAbbr(day)}/${day.slice(0, 4)}`;
const rangeK = (r: MeeRequest): string =>
  r.t0 && r.t1 ? `${fd(r.from)} ${r.t0}–${r.t1}` : r.type === 'fix' ? `${fd(r.from)} · ${r.sub}` : `${r.to && r.to !== r.from ? `${r.from.slice(8, 10)}–${fd(r.to)}` : fd(r.from)}${r.half ? ' · sáng' : ''}`;
const qtyK = (r: MeeRequest): string => (r.type === 'ot' ? `${num(otHours(r))} h` : r.type === 'fix' ? '—' : `${num(r.qty ?? 0)} ngày`);

function ApproveBody({ m }: { m: ReturnType<typeof useMee> }) {
  const t = useT();
  const toast = useToast();
  const phone = useLayout() === 'phone';
  const rid = useId();
  const me = m.me!;
  const [decided, setDecided] = useState<ReadonlySet<string>>(new Set());
  const [sel, setSel] = useState<ReadonlySet<string>>(new Set());
  const [rej, setRej] = useState<{ id: string; who: string } | null>(null);
  const [why, setWhy] = useState('');
  const [tried, setTried] = useState(false);

  const rows: MeeRequest[] = m.team.filter((r) => r.status === 'Pending' || decided.has(r.id));
  const open = pendingOfTeam(m.all, me.team);
  const openIds = open.map((r) => r.id);
  const picked = [...sel].filter((id) => openIds.includes(id));
  const seat = me.seats[0]![0];
  const from = '2026-10-19';
  const today = BOARD_TODAY;
  const grid = absenceGrid(m.cal, me.team, m.all, from, SPAN);

  const decide = (ids: string[], st: 'Approved' | 'Rejected') => {
    const undo = m.decide(ids, st);
    setDecided((d) => new Set([...d, ...ids]));
    setSel(new Set());
    toast({
      msg: t(st === 'Approved' ? 'meeAp.approvedN' : 'meeAp.rejectedN', { n: ids.length }),
      undo: () => {
        undo();
        setDecided((d) => new Set([...d].filter((x) => !ids.includes(x))));
      },
    });
  };
  const confirmReject = () => {
    setTried(true);
    if (!why.trim() || !rej) return;
    decide([rej.id], 'Rejected');
    setRej(null);
  };

  const cols: Column<MeeRequest>[] = [
    { key: 'who', header: t('meeAp.hSender'), cell: (r) => <span className={s.who}><span className={s.avatar} aria-hidden="true">{r.owner.slice(0, 2).toUpperCase()}</span><b>{r.owner}</b></span> },
    { key: 'type', header: '', width: 56, cell: (r) => <TypeIcon type={r.type} /> },
    { key: 'time', header: t('meeAp.hTime'), width: 190, cell: (r) => rangeK(r) },
    { key: 'qty', header: t('meeAp.hQty'), minor: true, cell: (r) => qtyK(r) },
    { key: 'bk', header: t('meeAp.hHand'), minor: true, cell: (r) => r.handover ?? '—' },
    { key: 'left', header: t('meeAp.hLeft'), minor: true, cell: (r) => <b>{leftLabel(m.cal, m.people, m.all, r)}</b> },
    { key: 'st', header: t('meeAp.hStatus'), cell: (r) => <StatusChip status={r.status} /> },
    {
      key: 'act',
      header: '',
      width: 210,
      align: 'right',
      cell: (r) =>
        r.status === 'Pending' ? (
          <span className={s.acts}>
            <Button size="sm" variant="ghost" icon={<IconX size={16} />} aria-label={t('meeAp.rejectAria', { id: r.id, who: r.owner })} onClick={() => { setRej({ id: r.id, who: r.owner }); setWhy(''); setTried(false); }}>{t('meeAp.reject')}</Button>
            <Button size="sm" variant="secondary" icon={<IconCheck size={16} />} aria-label={t('meeAp.approveAria', { id: r.id, who: r.owner })} onClick={() => decide([r.id], 'Approved')}>{t('meeAp.approve')}</Button>
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('meeAp.title')}
        actions={<Button variant="primary" icon={<IconChecklist size={16} />} onClick={() => (picked.length ? decide(picked, 'Approved') : toast({ msg: t('meeAp.pickOne') }))}>{picked.length ? t('meeAp.approveSelN', { n: picked.length }) : t('meeAp.approveSel')}</Button>}
      />
    <div className={s.page}>
      <section className={s.card} aria-label={t('meeAp.absence')}>
        <div className={s.head}>
          <b>{t('meeAp.absence')}</b>
          <span className={s.count}>{fd(from)} → {fd(grid[0]!.cells[SPAN - 1]!.day)}</span>
          <span className={s.grow} />
          <span className={s.legendRow}>
            <span><i className={s.dot} style={{ background: KIND.leave.color }} />{t('meeAp.kLeave')}</span>
            <span><i className={s.dot} style={{ background: KIND.trip.color }} />{t('meeAp.kTrip')}</span>
            <span><i className={`${s.dot} ${s.dotWait}`} style={{ background: KIND.leave.color, color: KIND.leave.color }} />{t('meeAp.kWait')}</span>
            <span><i className={`${s.dot} ${s.dotToday}`} />{t('meeAp.kToday')}</span>
          </span>
        </div>
        <div className={s.tlScroll}><div className={s.tl} style={{ gridTemplateColumns: `88px repeat(${SPAN}, minmax(0, 1fr))` }} role="table" aria-label={t('meeAp.absence')}>
          <span />
          {grid[0]!.cells.map((c) => (
            <span key={c.day} className={s.tlDay} data-today={c.day === today ? 1 : undefined} data-off={c.kind === 'off' ? 1 : undefined} role="columnheader">{dayHead(c.day)}</span>
          ))}
          {grid.map((row) => (
            <div key={row.id} className={s.tlRow} role="row">
              <span className={s.tlWho} role="rowheader">{row.id}</span>
              {row.cells.map((c) => (
                <span
                  key={c.day}
                  role="cell"
                  className={s.tlCell}
                  data-kind={c.kind || undefined}
                  data-wait={c.wait ? 1 : undefined}
                  data-today={c.day === today ? 1 : undefined}
                  style={c.kind && c.kind !== 'off' ? { background: KIND[c.kind === 'pending' ? 'leave' : c.kind].color, color: KIND[c.kind === 'pending' ? 'leave' : c.kind].color } : undefined}
                  title={`${row.id} · ${dayHead(c.day)}${c.kind ? ` · ${KIND[c.kind].k}` : ''}`}
                />
              ))}
            </div>
          ))}
        </div></div>
      </section>
      <section className={phone ? s.stack : `${s.card} ${s.tableCard}`} aria-label={t('meeAp.pendingList')}>
        {phone ? (
          rows.map((r) => (
            <div key={r.id} className={`${s.card} ${s.apCard}`}>
              <div className={s.reqCard}>
                <span className={s.avatar} aria-hidden="true">{r.owner.slice(0, 2).toUpperCase()}</span>
                <span className={s.main}>
                  <b>{r.owner}</b>
                  <span>{rangeK(r)} · {qtyK(r)}</span>
                </span>
                <StatusChip status={r.status} />
              </div>
              {r.status === 'Pending' && (
                <span className={s.acts}>
                  <Button size="sm" variant="ghost" icon={<IconX size={16} />} aria-label={t('meeAp.rejectAria', { id: r.id, who: r.owner })} onClick={() => { setRej({ id: r.id, who: r.owner }); setWhy(''); setTried(false); }}>{t('meeAp.reject')}</Button>
                  <Button size="sm" variant="secondary" icon={<IconCheck size={16} />} aria-label={t('meeAp.approveAria', { id: r.id, who: r.owner })} onClick={() => decide([r.id], 'Approved')}>{t('meeAp.approve')}</Button>
                </span>
              )}
            </div>
          ))
        ) : (
          <>
        <Table
          label={t('meeAp.pendingList')}
          columns={cols}
          rows={rows}
          rowKey={(r) => r.id}
          selected={sel}
          onSelect={(n) => setSel(new Set([...n].filter((id) => openIds.includes(id))))}
        />
          </>
        )}
        {rows.length === 0 && <div className={s.none} style={{ padding: 16 }}>{t('meeAp.none')}</div>}
        <div className={s.foot}>{t('meeAp.seat', { seat })} {me.id}</div>
      </section>
      <Modal
        open={!!rej}
        onOpenChange={(o) => !o && setRej(null)}
        title={rej ? t('meeAp.rejReq', { id: rej.id }) : t('meeAp.rejReq0')}
        description={rej ? t('meeAp.reasonTo', { who: rej.who }) : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRej(null)}>{t('meeAp.cancel')}</Button>
            <Button variant="danger" onClick={confirmReject}>{t('meeAp.reject')}</Button>
          </>
        }
      >
        <div className={s.field}>
          <label htmlFor={`${rid}-w`} className={s.fieldLb}>{t('meeAp.reason')} <span className={s.req}>*</span></label>
          <textarea id={`${rid}-w`} className={s.textarea} rows={3} value={why} data-bad={tried && !why.trim() ? 1 : undefined} aria-invalid={tried && !why.trim() ? true : undefined} placeholder={t('meeAp.rejPh')} onChange={(e) => setWhy(e.target.value)} />
          {tried && !why.trim() && <div className={s.drErr} role="alert">{t('meeAp.needReason')}</div>}
        </div>
      </Modal>
    </div>
    </>
  );
}

export function ApproveScreen() {
  const t = useT();
  const m = useMee();
  const nav = useNavigate();
  const forbidden = m.state === 'ready' && !m.me?.manager;
  return (
    <>
      {!(m.state === 'ready' && !forbidden) && <PageHeader title={t('meeAp.title')} />}
      <StateCard state={m.state}>
        <StateView
          state={forbidden ? 'noperm' : m.state}
          {...meeStateProps('appr', m.state, m.retry, () => nav({ to: '/mee' }))}
          {...(forbidden ? { title: 'Chỉ trưởng phòng duyệt đơn của nhóm', desc: undefined, action: undefined } : {})}
          skeleton={<div className={s.skel}><Skeleton rows={3} /><Skeleton rows={5} /></div>}
        >
          <ApproveBody m={m} />
        </StateView>
      </StateCard>
    </>
  );
}
